import React, { useMemo, useEffect, useState } from 'react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

const WINDOW_SIZE = 600; // Number of data points to show at once
const PACKET_HEADER = [170, 85, 15, 7]; // PPG packet header

const PPGVisualizer = React.memo(function PPGVisualizer({ rawData = [], showAll = false }) {
  const [isVisible, setIsVisible] = useState(false);

  // Process raw PPG data into chart format
  const processedData = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return [];
    }

    let ppgData = [];
    let xAxis = 0;

    try {
      // Process the raw data looking for PPG packets
      for (let i = 0; i <= rawData.length - 10; i++) {
        // Look for packet header
        if (rawData[i] === PACKET_HEADER[0] &&
          rawData[i + 1] === PACKET_HEADER[1] &&
          rawData[i + 2] === PACKET_HEADER[2] &&
          rawData[i + 3] === PACKET_HEADER[3]) {

          // Extract PPG values from packet (5 bytes after header)
          const ppgValues = rawData.slice(i + 5, i + 10);
          ppgData = [...ppgData, ...ppgValues.map((value, index) => ({
            x: xAxis + index,
            y: value > 128 ? value - 128 : value
          }))];
          xAxis += ppgValues.length;

          i += 9; // Skip processed packet
        }
      }

      // If no packet headers found, treat as raw PPG data
      if (ppgData.length === 0 && rawData.length > 0) {
        rawData.forEach((value, index) => {
          // Only add valid numerical values to prevent line breaks
          if (typeof value === 'number' && !isNaN(value) && value !== null && value !== undefined) {
            ppgData.push({
              x: index,
              y: value,
              time: Date.now() + index * 10
            });
          }
        });
      }

    } catch (error) {
      console.error('Error processing PPG data:', error);
      // Fallback: treat as raw data but filter out invalid values
      ppgData = rawData
        .map((value, index) => ({
          x: index,
          y: typeof value === 'number' && !isNaN(value) ? value : null,
          time: Date.now() + index * 10
        }))
        .filter(point => point.y !== null); // Remove null values
    }

    return ppgData;
  }, [rawData]);

  // Get visible data window
  const visibleData = useMemo(() => {
    if (showAll || processedData.length <= WINDOW_SIZE) {
      return processedData;
    }
    return processedData.slice(-WINDOW_SIZE);
  }, [showAll, processedData]);

  // Show/hide animation
  useEffect(() => {
    if (visibleData.length > 0) {
      setIsVisible(true);
    }
  }, [visibleData.length]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#18202E',
          border: '1px solid #00C8DA',
          borderRadius: '4px',
          padding: '8px',
          fontSize: '12px'
        }}>
          <p style={{ color: '#00C8DA', margin: 0, fontWeight: 'bold' }}>
            Sample: {label}
          </p>
          <p style={{ color: '#F0EEEE', margin: 0 }}>
            Value: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  if (!isVisible || visibleData.length === 0) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#A0A0A0',
        fontSize: '14px'
      }}>
        {rawData.length === 0 ? 'Waiting for PPG data...' : 'Processing signal...'}
      </div>
    );
  }

  return (
    <div className="ppg-visualizer" style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={visibleData}
          margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#2A3A4A" />
          <XAxis
            dataKey="x"
            type="number"
            scale="linear"
            domain={['dataMin', 'dataMax']}
            tick={false}
            axisLine={{ stroke: '#3A4A5A' }}
            tickLine={false}
          />
          <YAxis
            domain={['dataMin - 5', 'dataMax + 5']}
            tick={false}
            axisLine={{ stroke: '#3A4A5A' }}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="linear"
            dataKey="y"
            stroke="#00C8DA"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

export default PPGVisualizer;